// code to globally store ids , statuses and anything else required globally, subject to changes


import { createStore } from 'vuex';
import { fetch_form_metadata } from '@/services/form_metadata_service';

const AUTH_SESSION_KEYS = {
  currentUserId: 'current_user_id',
  currentLeadId: 'current_lead_id',
  currentRole: 'current_role',
  currentLeadName: 'current_lead_name',
};

const LAND_COURT_SESSION_KEYS = [
  'land_court_entity_type_id',
  'land_court_case_meta',
];

const LAND_COURT_SESSION_PREFIXES = [
  'land_court_caseload_snapshot:',
];

function read_session_value(key, fallback = null) {
  if (typeof window === 'undefined') {
    return fallback;
  }

  const value = sessionStorage.getItem(key);
  return value ?? fallback;
}

function read_session_number(key) {
  const value = read_session_value(key);

  if (value === null || value === '') {
    return null;
  }

  const normalized_value = Number(value);
  return Number.isNaN(normalized_value) ? null : normalized_value;
}

function write_session_value(key, value) {
  if (typeof window === 'undefined') {
    return;
  }

  if (value === null || value === undefined || value === '') {
    sessionStorage.removeItem(key);
    return;
  }

  sessionStorage.setItem(key, String(value));
}

function clear_auth_session() {
  if (typeof window === 'undefined') {
    return;
  }

  Object.values(AUTH_SESSION_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });
  LAND_COURT_SESSION_KEYS.forEach((key) => {
    sessionStorage.removeItem(key);
  });
  Object.keys(sessionStorage)
    .filter((key) => LAND_COURT_SESSION_PREFIXES.some((prefix) => key.startsWith(prefix)))
    .forEach((key) => {
      sessionStorage.removeItem(key);
    });
}

const getInitialState = () => ({
  currentId: null,
  currentStatus: null,
  currentUserId: read_session_number(AUTH_SESSION_KEYS.currentUserId),
  currentLeadId: read_session_number(AUTH_SESSION_KEYS.currentLeadId),
  currentRole: read_session_value(AUTH_SESSION_KEYS.currentRole),
  currentLeadName: read_session_value(AUTH_SESSION_KEYS.currentLeadName),
  landCourtCaseCount: null,
  currentFormId: null,
  entitiesById: {},
  questionTypes: [],
  formTypes: [],
  formStatuses: [],
  formMetadataLoading: false,
  formMetadataLoaded: false,
});

const normalizeEntity = (payload = {}) => {
  const id = payload.id ?? payload.workflowId ?? payload.itemId ?? null;
  const status = payload.status ?? payload.workflowStatus ?? null;

  const result = {
    ...payload,
    id,
    status,
  };
  return result
};

const store = createStore({
  state: getInitialState,

  getters: {
    currentId: (state) => state.currentId,
    currentStatus: (state) => state.currentStatus,
    currentUserId: (state) => state.currentUserId,
    currentLeadId: (state) => state.currentLeadId,
    currentRole: (state) => state.currentRole,
    currentLeadName: (state) => state.currentLeadName,
    landCourtCaseCount: (state) => state.landCourtCaseCount,
    currentFormId: (state) => state.currentFormId,
    questionTypes: (state) => state.questionTypes,
    formTypes: (state) => state.formTypes,
    formStatuses: (state) => state.formStatuses,
    formMetadataLoading: (state) => state.formMetadataLoading,
    formMetadataLoaded: (state) => state.formMetadataLoaded,
    currentEntity: (state) => {
      if (!state.currentId) {
        return null;
      }

      return state.entitiesById[state.currentId] ?? null;
    },
    entityById: (state) => (id) => state.entitiesById[id] ?? null,
    statusById: (state) => (id) => state.entitiesById[id]?.status ?? null,
    hasStatus: (state) => (status) => state.currentStatus === status,
  },

  mutations: {
    SET_CURRENT_ID(state, id) {
      state.currentId = id;
    },
    SET_CURRENT_STATUS(state, status) {
      state.currentStatus = status;
    },
    SET_CURRENT_USER_ID(state, id) {
      state.currentUserId = id;
      write_session_value(AUTH_SESSION_KEYS.currentUserId, id);
    },
    SET_CURRENT_LEAD_ID(state, id) {
      state.currentLeadId = id;
      write_session_value(AUTH_SESSION_KEYS.currentLeadId, id);
    },
    SET_CURRENT_ROLE(state, role) {
      state.currentRole = role;
      write_session_value(AUTH_SESSION_KEYS.currentRole, role);
    },
    SET_CURRENT_LEAD_NAME(state, leadName) {
      state.currentLeadName = leadName;
      write_session_value(AUTH_SESSION_KEYS.currentLeadName, leadName);
    },
    SET_LAND_COURT_CASE_COUNT(state, count) {
      const normalized_count = Number(count);
      state.landCourtCaseCount = Number.isFinite(normalized_count) && normalized_count >= 0
        ? normalized_count
        : 0;
    },
    SET_CURRENT_FORM_ID(state, id) {
      state.currentFormId = id;
    },
    SET_FORM_METADATA_LOADING(state, isLoading) {
      state.formMetadataLoading = isLoading;
    },
    SET_FORM_METADATA_LOADED(state, isLoaded) {
      state.formMetadataLoaded = isLoaded;
    },
    SET_QUESTION_TYPES(state, questionTypes) {
      state.questionTypes = Array.isArray(questionTypes) ? questionTypes : [];
    },
    SET_FORM_TYPES(state, formTypes) {
      state.formTypes = Array.isArray(formTypes) ? formTypes : [];
    },
    SET_FORM_STATUSES(state, formStatuses) {
      state.formStatuses = Array.isArray(formStatuses) ? formStatuses : [];
    },
    UPSERT_ENTITY(state, payload) {
      const entity = normalizeEntity(payload);

      if (!entity.id) {
        return;
      }

      state.entitiesById = {
        ...state.entitiesById,
        [entity.id]: {
          ...(state.entitiesById[entity.id] ?? {}),
          ...entity,
        },
      };
    },
    SET_ACTIVE_ENTITY(state, payload) {
      const entity = normalizeEntity(payload);

      if (!entity.id) {
        return;
      }

      state.entitiesById = {
        ...state.entitiesById,
        [entity.id]: {
          ...(state.entitiesById[entity.id] ?? {}),
          ...entity,
        },
      };
      state.currentId = entity.id;
      state.currentStatus = entity.status;
    },
    CLEAR_ACTIVE_ENTITY(state) {
      state.currentId = null;
      state.currentStatus = null;
    },
    RESET_STORE(state) {
      clear_auth_session();
      Object.assign(state, getInitialState());
    },
  },

  actions: {
    setCurrentId({ commit }, id) {
      commit('SET_CURRENT_ID', id);
    },
    setCurrentStatus({ commit }, status) {
      commit('SET_CURRENT_STATUS', status);
    },
    setCurrentUserId({ commit }, id) {
      commit('SET_CURRENT_USER_ID', id);
    },
    setCurrentLeadId({ commit }, id) {
      commit('SET_CURRENT_LEAD_ID', id);
    },
    setCurrentRole({ commit }, role) {
      commit('SET_CURRENT_ROLE', role);
    },
    setCurrentLeadName({ commit }, leadName) {
      commit('SET_CURRENT_LEAD_NAME', leadName);
    },
    setLandCourtCaseCount({ commit }, count) {
      commit('SET_LAND_COURT_CASE_COUNT', count);
    },
    setCurrentFormId({ commit }, id) {
      commit('SET_CURRENT_FORM_ID', id);
    },
    setQuestionTypes({ commit }, questionTypes) {
      commit('SET_QUESTION_TYPES', questionTypes);
    },
    setFormTypes({ commit }, formTypes) {
      commit('SET_FORM_TYPES', formTypes);
    },
    setFormStatuses({ commit }, formStatuses) {
      commit('SET_FORM_STATUSES', formStatuses);
    },
    upsertEntity({ commit }, payload) {
      commit('UPSERT_ENTITY', payload);
    },
    setActiveEntity({ commit }, payload) {
      commit('SET_ACTIVE_ENTITY', payload);
    },
    syncStatusFromBackend({ commit }, payload) {
      commit('SET_ACTIVE_ENTITY', payload);
    },
    clearActiveEntity({ commit }) {
      commit('CLEAR_ACTIVE_ENTITY');
    },
    async loadFormMetadata({ state, commit }, { force = false } = {}) {
      if (state.formMetadataLoading) {
        return;
      }

      if (state.formMetadataLoaded && !force) {
        return;
      }

      commit('SET_FORM_METADATA_LOADING', true);

      try {
        const metadata = await fetch_form_metadata();

        commit('SET_QUESTION_TYPES', metadata?.question_types || []);
        commit('SET_FORM_TYPES', metadata?.form_types || []);
        commit('SET_FORM_STATUSES', metadata?.form_statuses || []);
        commit('SET_FORM_METADATA_LOADED', true);
      } finally {
        commit('SET_FORM_METADATA_LOADING', false);
      }
    },
    ensureFormMetadataLoaded({ state, dispatch }) {
      if (state.formMetadataLoaded) {
        return Promise.resolve();
      }

      return dispatch('loadFormMetadata');
    },
    refreshFormMetadata({ dispatch }) {
      return dispatch('loadFormMetadata', { force: true });
    },
    resetStore({ commit }) {
      commit('RESET_STORE');
    },
  },
});

export default store;
